import { api } from './client'

/**
 * F-138: client di `LibraryDocumentsController` (`/api/library`): la libreria di documenti condivisi. Lettura per ogni
 * utente autenticato (il server mostra solo i documenti validi e visibili per ruolo e ramo); caricamento, modifica,
 * nuova versione e ritiro solo per backoffice, direzione e admin (403 per gli altri).
 *
 * Come `documents.ts`, il caricamento e' un `multipart/form-data` e quindi non passa da `api()` (che imposta sempre
 * `Content-Type: application/json`): il browser deve scrivere da solo il boundary.
 */

export const LIBRARY_CATEGORIES = ['Presentazione', 'Documento utile', 'Listino prezzi', 'Altro'] as const
export type LibraryCategory = (typeof LIBRARY_CATEGORIES)[number]

export type LibraryStatus = 'Valido' | 'Scaduto' | 'NonAncoraValido'

export interface LibraryDocument {
  id: number
  title: string
  description: string | null
  category: LibraryCategory
  /** ISO `date` (es. `2026-10-10`). */
  validFrom: string
  validTo: string | null
  status: LibraryStatus
  isLatestVersion: boolean
  supersedesId: number | null
  originalFileName: string
  mimeType: string
  sizeBytes: number
  createdAt: string
  updatedAt: string | null
  /** Solo per chi gestisce la libreria; `null` per gli altri. Vuoto = tutti. */
  roles: string[] | null
  departmentIds: number[] | null
  uploadedById: number | null
}

export interface LibraryVersion {
  id: number
  title: string
  validFrom: string
  validTo: string | null
  originalFileName: string
  createdAt: string
  isThis: boolean
}

export interface LibraryDepartment {
  id: number
  name: string
  isActive: boolean
}

export interface LibraryOptions {
  canManage: boolean
  categories: LibraryCategory[]
  departments: LibraryDepartment[]
  roles: string[]
  allowedExtensions: string[]
  maxBytes: number
}

export interface LibraryListFilters {
  category?: string
  departmentId?: number
  q?: string
  /** Solo chi gestisce la libreria: aggiunge scaduti, non ancora validi e versioni sostituite. */
  includeExpired?: boolean
}

export interface LibraryUploadData {
  title: string
  category: string
  description?: string
  validFrom?: string
  validTo?: string
  roles?: string[]
  departmentIds?: number[]
}

export interface LibraryUpdateData {
  title: string
  category: string
  description: string | null
  validFrom: string | null
  validTo: string | null
  roles: string[]
  departmentIds: number[]
}

export interface LibraryNewVersionData {
  title?: string
  description?: string
  validFrom?: string
  validTo?: string
}

async function csrfHeaders(): Promise<HeadersInit> {
  const res = await fetch('/api/antiforgery/token', { credentials: 'include' })
  if (!res.ok) return {}
  const data = (await res.json()) as { token: string }
  return { 'X-XSRF-TOKEN': data.token }
}

async function readErrorMessage(res: Response): Promise<string> {
  const body = (await res.json().catch(() => null)) as { title?: string; message?: string; error?: string } | null
  return body?.message ?? body?.title ?? body?.error ?? `HTTP ${res.status}`
}

async function postForm<T>(url: string, form: FormData): Promise<T> {
  const res = await fetch(url, { method: 'POST', credentials: 'include', headers: await csrfHeaders(), body: form })
  if (!res.ok) throw new Error(await readErrorMessage(res))
  return res.json() as Promise<T>
}

export const libraryApi = {
  options: () => api<LibraryOptions>('/api/library/options'),

  list: (filters: LibraryListFilters = {}) => {
    const q = new URLSearchParams()
    if (filters.category) q.set('category', filters.category)
    if (filters.departmentId) q.set('departmentId', String(filters.departmentId))
    if (filters.q?.trim()) q.set('q', filters.q.trim())
    if (filters.includeExpired) q.set('includeExpired', 'true')
    const qs = q.toString()
    return api<LibraryDocument[]>(`/api/library${qs ? `?${qs}` : ''}`)
  },

  get: (id: number) =>
    api<{ document: LibraryDocument; versions: LibraryVersion[] | null }>(`/api/library/${id}`),

  /** Percorso same-origin dello scaricamento (sempre un allegato), da usare come `href`. */
  contentUrl: (id: number): string => `/api/library/${id}/content`,

  upload: (data: LibraryUploadData, file: File) => {
    const form = new FormData()
    form.append('Title', data.title)
    form.append('Category', data.category)
    if (data.description) form.append('Description', data.description)
    if (data.validFrom) form.append('ValidFrom', data.validFrom)
    if (data.validTo) form.append('ValidTo', data.validTo)
    for (const r of data.roles ?? []) form.append('Roles', r)
    for (const d of data.departmentIds ?? []) form.append('DepartmentIds', String(d))
    form.append('File', file)
    return postForm<{ id: number }>('/api/library', form)
  },

  update: (id: number, data: LibraryUpdateData) =>
    api<void>(`/api/library/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  newVersion: (id: number, data: LibraryNewVersionData, file: File) => {
    const form = new FormData()
    if (data.title) form.append('Title', data.title)
    if (data.description) form.append('Description', data.description)
    if (data.validFrom) form.append('ValidFrom', data.validFrom)
    if (data.validTo) form.append('ValidTo', data.validTo)
    form.append('File', file)
    return postForm<{ id: number; supersedesId: number }>(`/api/library/${id}/new-version`, form)
  },

  /** Ritiro logico: il documento sparisce dagli elenchi, il file resta sull'archivio. */
  retire: (id: number) => api<void>(`/api/library/${id}`, { method: 'DELETE' }),
}
